import {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import {DonutGraph} from "../DonutGraph/DonutGraph";
import {variantDataRetrieverMapDonutGraph} from "../DonutGraph/donutGraphDataRetrieving";
import {variantRawDataProcessorMapDonutGraph} from "../DonutGraph/donutGraphDataProcessing";
import {useDashboardJobScope} from "../../../views/JobContextProvider/useDashboardJobScope";

export const DoubleDonutGraph = ({variant, rawData: externalRawData, onDataState}) => {
    const variantToTitleMap = {
        defValue: "Default Data",
        storagePolicies: "Storage Policies",
        backup: "Backup",
        undefined: "Default Data"
    };

    const [rawData, setRawData] = useState(externalRawData ?? []);
    const [loading, setLoading] = useState(false);
    /*
     * The scope both donuts report on — the jobs ticked in the header, or every job the selector lists.
     *
     * Shared with the other header-scoped cards rather than read straight off the context, so that this
     * card and the Media Metrics card beside it cannot mean different things by 'All Jobs'; see
     * useDashboardJobScope for what each of the three answers is and why. It matters more here than
     * elsewhere because these figures come from the `xag_*` aggregates, where an unscoped query picks up
     * both the per-job rows and the company-wide roll-up row and adds them together.
     */
    const {jobIds, cid, jobIdsKey, waiting, nothingOnboarded} = useDashboardJobScope();

    useEffect(() => {
        if (externalRawData !== undefined) {
            setRawData(externalRawData);
            return;
        }
        // The job list is still on its way, so the scope is not known yet. Held on the spinner the donuts
        // already show rather than asking a question whose answer would be replaced a moment later.
        if (waiting) {
            setLoading(true);
            return;
        }
        // Nothing onboarded: no query to run. Empty rather than unscoped, which is the whole point of the
        // scope — the donuts draw their own 'No Data' from this.
        if (nothingOnboarded) {
            setRawData([]);
            setLoading(false);
            return;
        }
        let isMounted = true;
        const controller = new AbortController();
        const onAbort = () => {
            if (isMounted) setLoading(false);
        };
        controller.signal.addEventListener('abort', onAbort, {once: true});

        const dataRetrieverFn = variantDataRetrieverMapDonutGraph[variant] ?? variantDataRetrieverMapDonutGraph.defValue;
        // `background` keeps this out of the full-window wash the axios interceptor raises — the two donuts
        // below already show their own spinners from this same `loading`, so the wash only added a sheet
        // over the whole portal that ate every click. See utils/loadingWash.js.
        dataRetrieverFn(setRawData, setLoading, jobIds, cid, {signal: controller.signal, background: true});

        return () => {
            isMounted = false;
            controller.abort();
            controller.signal.removeEventListener('abort', onAbort);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, externalRawData, jobIdsKey, cid, waiting, nothingOnboarded]);

    /*
     * Tells the dashboard whether either donut has anything to draw, so an empty card can be left out. Decided
     * with the same processing each DonutGraph runs before choosing between its chart and 'No data', so the
     * card is only called empty when both halves would say so.
     */
    useEffect(() => {
        if (!onDataState) return;
        if (loading) {
            onDataState('loading');
            return;
        }
        const process = variantRawDataProcessorMapDonutGraph[variant] ?? variantRawDataProcessorMapDonutGraph.defValue;
        const hasData = ['Size', 'Files'].some(metric => process(rawData ?? [], metric).processedData.length > 0);
        onDataState(hasData ? 'data' : 'empty');
    }, [loading, rawData, variant, onDataState]);

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'row',
                overflow: 'visible',
            }}>
            <DonutGraph
                variant={variant}
                variantMetric={"Size"}
                title={`${variantToTitleMap[variant] ?? variantToTitleMap.defValue} - Size`}
                rawData={rawData}
                loading={loading}
            />
            <DonutGraph
                variant={variant}
                variantMetric={"Files"}
                title={`${variantToTitleMap[variant] ?? variantToTitleMap.defValue} - Files`}
                rawData={rawData}
                loading={loading}
            />
        </Box>);
};